/**
 * Adds an internal token to a URL if the URL contains the '{it}' placeholder.
 *
 * @param {string} url - The URL to add the internal token to
 * @param {string} token - The internal token to be added
 * @param {Object} user - The user object
 *
 * @returns {string} The URL with the internal token added if the placeholder exists and token/user are provided,
 *                   or the URL with the placeholder removed if token/user are not provided,
 *                   or the original URL if no placeholder exists
 */
export const addInternalToken = (url, token, user) => {
  if (!url.includes('{it}')) return url

  const separator = url.includes('?') ? '&' : '?'
  return token && user
    ? url.replace('{it}', `${separator}it=${token}`)
    : url.replace('{it}', '')
}

/**
 * Recursively adds internal tokens to URLs within a menu data structure.
 *
 * @param {Array|Object} dataSet - The menu data structure to process. Can be an array or object.
 * @param {string|null} token - The internal token to add to the URLs.
 * @param {Object|null} user - The user object containing user information.
 * @returns {Array|Object} A new data structure with internal tokens added to all URLs.
 *
 * @description
 * The function handles:
 * - Direct 'to' properties in menu items
 * - Nested 'to' properties within featureFlagFallback objects
 * - Recursive processing of nested objects and arrays
 */
export const recursiveAddTokenToMenus = (dataSet, token, user) => {
  // Deep copy the dataset to ensure immutability
  const newDataSet = Array.isArray(dataSet) ? [...dataSet] : { ...dataSet }

  for (let key of Object.keys(newDataSet)) {
    const data = newDataSet[key]

    if (typeof data === 'object' && data !== null) {
      if (data?.to || data?.featureFlagFallback?.to) {
        // Add internal token to the URL
        if (data?.to) {
          const to = addInternalToken(data?.to, token, user)
          newDataSet[key] = { ...data, to } // Create a new object with the updated property
        }

        // Add internal token to featureFlagFallback.to
        if (data?.featureFlagFallback?.to) {
          const to = addInternalToken(data.featureFlagFallback.to, token, user)
          newDataSet[key] = {
            ...data,
            featureFlagFallback: {
              ...data.featureFlagFallback,
              to
            }
          }
        }
      } else {
        // Recursively process nested objects or arrays
        newDataSet[key] = recursiveAddTokenToMenus(data, token, user)
      }
    }
  }

  return newDataSet
}
